import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { type ComponentDynamicSvgArgs, degrees2radians, type Island, Vertex2 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { DisplayMode } from '@r-core';
import { getCutoutWorldPosition } from '../helpers/get-cutout-world-position';
import { useDraggableSvg } from '@r-services/ui-interactions';

export type RenderCountertopCutoutDetailBlocksProps = {
	island: Island;
	// Degrees to counter-rotate the block's content by so callout text stays upright when an
	// ancestor rotates this island's whole subtree. Countertop page: 0.
	counterRotationDeg?: number;
};

// Roughly the 4-5x magnification approved in the Slack mockup for GitHub #282/#385 -- applied
// to the geometry only (see DetailBlock), never to the callout text itself.
const DETAIL_BLOCK_SCALE = 4;
// Clears the centerline-oval band below the countertop (draw-appliance-group-center-dimension.tsx
// puts those at ~DIMENSION_CENTER_LINE_INITIAL_Y + islandHeight/2, plus up to +4 for passthroughs).
const ROW_GAP = 12;
const BLOCK_GAP = 3;
const LEADER_STROKE_WIDTH = 0.15;
const MIN_SCALE = 1;
const MAX_SCALE = 12;
// Model-space equivalents of the annotation overlay's 8px handle / 2px selection pad.
const HANDLE_SIZE = 1.25;
const SELECTION_PAD = 0.5;
const SELECTION_COLOR = '#3182ce';
const SELECTION_DASH = '1,0.75';
const ZERO_BOUNDS: MeasuredBounds = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
const DRAG_BBOX = { origin: new Vertex2(), size: { width: 1000, height: 1000, depth: 0 }, padding: [0, 0, 0, 0] } as const;

type DetailTarget = {
	id: string;
	renderItem: (args: ComponentDynamicSvgArgs) => React.ReactElement;
	diameter: number;
	leaderFrom: { x: number; z: number };
};

type MeasuredBounds = {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
};

type PivotPoint = {
	x: number;
	y: number;
};

type BlockOffset = {
	dx: number;
	dy: number;
};

const parseOffset = (raw: string | undefined): BlockOffset | null => {
	if (!raw) return null;
	try {
		const { dx = 0, dy = 0 } = JSON.parse(raw) as Partial<BlockOffset>;
		return { dx, dy };
	} catch (_error) {
		return null;
	}
};

const parseScale = (raw: string | undefined): number => {
	const parsed = Number(raw);
	if (!raw || Number.isNaN(parsed)) return DETAIL_BLOCK_SCALE;
	return Math.min(Math.max(parsed, MIN_SCALE), MAX_SCALE);
};

// Rotates `point` by `deg` around `pivot`, matching an SVG `rotate(deg, pivot.x, pivot.y)` transform.
// Used to find where a block's corner actually lands once its content is counter-rotated around that pivot.
const rotateAroundPivot = (point: PivotPoint, pivot: PivotPoint, deg: number): PivotPoint => {
	if (deg === 0) return point;
	const rad = degrees2radians(deg);
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	const dx = point.x - pivot.x;
	const dy = point.y - pivot.y;
	return {
		x: pivot.x + (dx * cos) - (dy * sin),
		y: pivot.y + (dx * sin) + (dy * cos)
	};
};

// cx/cy place the handle as a 0|1 fraction of the block; each corner resizes about the opposite one.
type CornerSpec = {
	name: string;
	cx: 0 | 1;
	cy: 0 | 1;
	cursor: string;
};

const CORNERS: Array<CornerSpec> = [
	{ name: 'top-left', cx: 0, cy: 0, cursor: 'nwse-resize' },
	{ name: 'top-right', cx: 1, cy: 0, cursor: 'nesw-resize' },
	{ name: 'bottom-left', cx: 0, cy: 1, cursor: 'nesw-resize' },
	{ name: 'bottom-right', cx: 1, cy: 1, cursor: 'nwse-resize' }
];

type ResizeHandleProps = {
	blockId: string;
	corner: CornerSpec;
	originX: number;
	originY: number;
	blockWidth: number;
	blockHeight: number;
	counterRotationDeg: number;
	onResizeStart: (corner: CornerSpec, pointerX: number) => void;
	onResize: (pointerX: number, commit: boolean) => void;
};

const ResizeHandle = (props: ResizeHandleProps): React.ReactElement => {
	const { blockId, corner, originX, originY, blockWidth, blockHeight, counterRotationDeg, onResizeStart, onResize } = props;
	const ref = useRef<SVGRectElement>(null);
	const cx = originX + (blockWidth * corner.cx);
	const cy = originY + (blockHeight * corner.cy);

	useDraggableSvg({
		ref,
		enabled: [true, true],
		cursor: ['inherit', corner.cursor],
		position: { x: cx, y: cy },
		bBox: { ...DRAG_BBOX, padding: [0, 0, 0, 0] },
		parentRotation: counterRotationDeg,
		onDragStart: (event) => onResizeStart(corner, event?.position.new.x ?? cx),
		onDragging: (event) => {
			if (!event) return true;
			onResize(event.position.new.x, false);
			return true;
		},
		onDragEnd: (event) => {
			if (!event) return true;
			onResize(event.position.new.x, true);
			return true;
		}
	});

	return <rect
		ref={ref}
		id={`${blockId}-${corner.name}`}
		x={cx - (HANDLE_SIZE / 2)} y={cy - (HANDLE_SIZE / 2)}
		width={HANDLE_SIZE} height={HANDLE_SIZE}
		fill="white" stroke={SELECTION_COLOR} strokeWidth={LEADER_STROKE_WIDTH}
		style={{ cursor: corner.cursor, userSelect: 'none', pointerEvents: 'auto' }}
	/>;
};

// Frozen at drag start. The measured bbox mixes scale-dependent geometry with constant-size text,
// so it never grows proportionally -- the ratio must be taken against a fixed basis, not the live one.
type ResizeSession = {
	corner: CornerSpec;
	startScale: number;
	startBlockWidth: number;
	startPointerX: number;
	moved: boolean;
	anchorX: number;
	anchorY: number;
	commit: boolean;
};

type DetailBlockProps = {
	panelItemId: string;
	renderItem: (args: ComponentDynamicSvgArgs) => React.ReactElement;
	diameter: number;
	fontSize: number;
	leaderFrom: { x: number; z: number };
	rowX: number;
	slotX: number;
	slotY: number;
	counterRotationDeg: number;
	onMeasured: (width: number) => void;
};

/**
 * One magnified copy of a cutout's `.svg.item()` plus its leader back to the real cutout.
 * The auto-layout slot is only the default: a manual drag persists as an offset and a manual
 * resize persists as a scale, both in the kitchen's blueprintOverrides keyed by panel-item id —
 * the same override store the CLEAN EDGE labels and EditableText fields use. Selecting it shows
 * the same dashed box + corner handles the SKU annotation shapes use in this view, and the leader
 * always draws to the top centre of that box. Read-only pages render it static and unselectable.
 */
const DetailBlock = (props: DetailBlockProps): React.ReactElement => {
	const { panelItemId, renderItem, diameter, fontSize, leaderFrom, rowX, slotX, slotY, counterRotationDeg, onMeasured } = props;
	const { controller } = useContext(AppContext);
	const { kitchen, options } = controller;
	const contentRef = useRef<SVGGElement>(null);
	const groupRef = useRef<SVGGElement>(null);
	const [bounds, setBounds] = useState<MeasuredBounds>(ZERO_BOUNDS);
	const [selected, setSelected] = useState(false);
	const resizeRef = useRef<ResizeSession | null>(null);

	// Namespaced -- render-countertop-panel-item.tsx already renders <g id={panelItem.id}> for the same item.
	const blockId = `cutout-detail-${panelItemId}`;
	const posKey = `cutoutDetail.pos.${panelItemId}`;
	const scaleKey = `cutoutDetail.scale.${panelItemId}`;
	const [offset, setOffset] = useState<BlockOffset | null>(parseOffset(kitchen?.blueprintOverrides?.[posKey]));
	const [scale, setScale] = useState<number>(parseScale(kitchen?.blueprintOverrides?.[scaleKey]));

	const draggable = !options.readonly;
	const originX = offset ? rowX + offset.dx : slotX;
	const originY = offset ? slotY + offset.dy : slotY;
	const blockWidth = bounds.maxX - bounds.minX;
	const blockHeight = bounds.maxY - bounds.minY;

	useLayoutEffect(() => {
		const node = contentRef.current;
		if (!node) return;
		try {
			const box = node.getBBox();
			if (box.width <= 0 || box.height <= 0) return;
			const next = { minX: box.x, minY: box.y, maxX: box.x + box.width, maxY: box.y + box.height };
			setBounds(next);
			onMeasured(next.maxX - next.minX);

			// Re-anchor against the freshly measured size so the opposite corner holds still, whatever
			// the non-proportional growth actually turned out to be.
			const session = resizeRef.current;
			if (session) {
				const width = next.maxX - next.minX;
				const height = next.maxY - next.minY;
				const nextOriginX = session.corner.cx === 0 ? session.anchorX - width : session.anchorX;
				const nextOriginY = session.corner.cy === 0 ? session.anchorY - height : session.anchorY;
				const nextOffset = { dx: nextOriginX - rowX, dy: nextOriginY - slotY };
				setOffset(nextOffset);
				if (session.commit) {
					resizeRef.current = null;
					kitchen?.setBlueprintOverride(posKey, JSON.stringify(nextOffset));
				}
			}
		} catch (_error) {
			// getBBox can throw before the node has layout -- the fallback bounds already rendered above stay in place
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderItem, fontSize, diameter, scale, rowX, slotY]);

	// Click-away deselects, matching how the annotation overlay drops its selection.
	useEffect(() => {
		if (!selected) return undefined;
		const onPointerDown = (event: PointerEvent): void => {
			const node = groupRef.current;
			if (node && event.target instanceof Node && node.contains(event.target)) return;
			setSelected(false);
		};
		document.addEventListener('pointerdown', onPointerDown);
		return () => document.removeEventListener('pointerdown', onPointerDown);
	}, [selected]);

	useDraggableSvg({
		ref: groupRef,
		enabled: [draggable, draggable],
		cursor: ['inherit', 'move'],
		position: { x: originX, y: originY },
		bBox: { ...DRAG_BBOX, padding: [0, 0, 0, 0] },
		parentRotation: counterRotationDeg,
		onDragging: (event) => {
			// A corner handle sits inside this same <g>, so a ctrl+click can still arm both hooks -- the resize wins.
			if (resizeRef.current) return false;
			if (!event) return true;
			setOffset({ dx: event.position.new.x - rowX, dy: event.position.new.y - slotY });
			return true;
		},
		onDragEnd: (event) => {
			if (resizeRef.current) return false;
			if (!event) return true;
			const { old: previous, new: next } = event.position;
			// A click that only selects the block never moves the pointer -- persisting it would dirty the design.
			if (next.x === previous.x && next.y === previous.y) return true;
			kitchen?.setBlueprintOverride(posKey, JSON.stringify({ dx: next.x - rowX, dy: next.y - slotY }));
			return true;
		}
	});

	const beginResize = (corner: CornerSpec, pointerX: number): void => {
		resizeRef.current = {
			corner,
			startScale: scale,
			startBlockWidth: blockWidth,
			startPointerX: pointerX,
			moved: false,
			anchorX: originX + (blockWidth * (corner.cx === 0 ? 1 : 0)),
			anchorY: originY + (blockHeight * (corner.cy === 0 ? 1 : 0)),
			commit: false
		};
	};

	// Only sets scale; the measure effect above repositions once the real new size is known, and
	// persists on the commit pass so the stored offset is the re-anchored one, not a stale frame.
	const applyResize = (pointerX: number, commit: boolean): void => {
		const session = resizeRef.current;
		if (!session || session.startBlockWidth <= 0) return;
		if (pointerX !== session.startPointerX) session.moved = true;
		// Same reason as onDragEnd -- a handle press with no travel is a selection, not a resize.
		if (!session.moved) {
			if (commit) resizeRef.current = null;
			return;
		}
		const ratio = Math.abs(pointerX - session.anchorX) / session.startBlockWidth;
		const nextScale = Math.min(Math.max(session.startScale * ratio, MIN_SCALE), MAX_SCALE);
		if (!commit) {
			setScale(nextScale);
			return;
		}
		kitchen?.setBlueprintOverride(scaleKey, String(nextScale));
		if (nextScale === scale) {
			// No re-measure will follow, so nothing left to re-anchor -- persist what's on screen now.
			resizeRef.current = null;
			kitchen?.setBlueprintOverride(posKey, JSON.stringify({ dx: originX - rowX, dy: originY - slotY }));
			return;
		}
		session.commit = true;
		setScale(nextScale);
	};

	// Scale lives one level below contentRef (getBBox excludes an element's own transform).
	// fontSize is pre-divided by that scale so text -- unlike the geometry -- ends up at its normal, legible size.
	const content = <g ref={contentRef}>
		<g transform={`scale(${scale})`}>
			{renderItem({ fontSize: fontSize / scale, diameter })}
		</g>
	</g>;

	const translateX = originX - bounds.minX;
	const translateY = originY - bounds.minY;
	const leaderTo = rotateAroundPivot(
		{ x: originX + (blockWidth / 2), y: originY - SELECTION_PAD },
		{ x: originX, y: originY },
		counterRotationDeg
	);
	const showChrome = draggable && selected && blockWidth > 0;

	return <g>
		<line
			x1={leaderFrom.x} y1={leaderFrom.z}
			x2={leaderTo.x} y2={leaderTo.y}
			stroke="black" strokeWidth={LEADER_STROKE_WIDTH} fill="none"
		/>
		<g ref={groupRef} id={blockId} style={{ userSelect: 'none' }} onMouseDown={() => { if (draggable) setSelected(true); }}>
			<g transform={`rotate(${counterRotationDeg}, ${originX}, ${originY}) translate(${translateX}, ${translateY})`}>
				{/* A <g> only takes pointer events on its painted children, so the callout text was the
				    only grabbable surface -- dragging between glyphs selected text instead of moving. */}
				{blockWidth > 0 && <rect
					x={bounds.minX} y={bounds.minY} width={blockWidth} height={blockHeight}
					fill="transparent" style={{ pointerEvents: 'auto' }}
				/>}
				{content}
			</g>
			{showChrome && <g transform={`rotate(${counterRotationDeg}, ${originX}, ${originY})`}>
				<rect
					x={originX - SELECTION_PAD} y={originY - SELECTION_PAD}
					width={blockWidth + (SELECTION_PAD * 2)} height={blockHeight + (SELECTION_PAD * 2)}
					fill="none" stroke={SELECTION_COLOR} strokeWidth={LEADER_STROKE_WIDTH} strokeDasharray={SELECTION_DASH}
					style={{ pointerEvents: 'none' }}
				/>
				{CORNERS.map((corner) => <ResizeHandle
					key={corner.name}
					blockId={blockId}
					corner={corner}
					originX={originX} originY={originY}
					blockWidth={blockWidth} blockHeight={blockHeight}
					counterRotationDeg={counterRotationDeg}
					onResizeStart={beginResize}
					onResize={applyResize}
				/>)}
			</g>}
		</g>
	</g>;
};

// Off-panel detail blocks for cutouts whose component record opts into `svg.offPanelDetail`
// (GitHub #282/#385). Driven entirely off that flag -- a future SKU (e.g. C1SINK1618) opts in with no code change here.
export const RenderCountertopCutoutDetailBlocks = (props: RenderCountertopCutoutDetailBlocksProps): React.ReactElement => {
	const { island, counterRotationDeg = 0 } = props;
	const { controller: { options, navigation } } = useContext(AppContext);
	const fontSize = DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor;

	// Countertop manufacturing sheets only. The overhead-layout family deliberately shows the bare
	// cutout with no callouts and no detail block at all (GitHub #282/#385 follow-up).
	const isCountertopManufacturingView = navigation.displayMode === DisplayMode.Manufacturing
		|| navigation.displayMode === DisplayMode.InlineManufacturing;

	// Not memoized: island/panel/panelItem are mutable class instances, so caching by reference identity would go stale on in-place mutation.
	// Re-renders already happen on the relevant domain events; this walk is cheap.
	const targets: Array<DetailTarget> = [];
	if (isCountertopManufacturingView) {
		island.panels.children.forEach((panel) => {
			panel.panelItemGroups.children.forEach((group) => {
				group.items.children.forEach((panelItem) => {
					const { svg } = panelItem.item.component;
					if (!panelItem.boundTop || !svg?.offPanelDetail || !svg.item) return;
					targets.push({
						id: panelItem.id,
						renderItem: svg.item,
						diameter: panelItem.item.cutout.width,
						leaderFrom: getCutoutWorldPosition(panel, group, panelItem)
					});
				});
			});
		});
	}

	const [widths, setWidths] = useState<Record<string, number>>({});

	if (targets.length === 0) return <></>;

	const { size: countertopSize, center: countertopCenter } = island.getCountertop();
	const halfWidth = countertopSize.width / 2;
	const halfDepth = countertopSize.height / 2;

	const rowY = countertopCenter.z + halfDepth + ROW_GAP;
	const rowX = countertopCenter.x - halfWidth;
	let cursorX = rowX;

	return <>
		{targets.map((target) => {
			const slotX = cursorX;
			cursorX += (widths[target.id] ?? 0) + BLOCK_GAP;

			return <DetailBlock
				key={target.id}
				panelItemId={target.id}
				renderItem={target.renderItem}
				diameter={target.diameter}
				fontSize={fontSize}
				leaderFrom={target.leaderFrom}
				rowX={rowX}
				slotX={slotX}
				slotY={rowY}
				counterRotationDeg={counterRotationDeg}
				onMeasured={(width) => setWidths((prev) => (prev[target.id] === width ? prev : { ...prev, [target.id]: width }))}
			/>;
		})}
	</>;
};
